import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./AddTransactionPage.module.css";
import { FaArrowLeft } from "react-icons/fa";

const AddTransactionPage = () => {
  const { t, i18n } = useTranslation();
  const { employeeId, transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, userData } = useContext(UserContext);

  const isEditMode = !!transactionId;
  const transactionToEdit = location.state?.transactionToEdit;
  const returnPath = location.state?.returnPath;

  const [transactionType, setTransactionType] = useState("salaryPayment");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [totalMonths, setTotalMonths] = useState("");
  const [monthlyDeduction, setMonthlyDeduction] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && transactionToEdit) {
      // Assuming transactionToEdit for an advance contains both payment and advance details
      const tx = transactionToEdit.payment || transactionToEdit; // Adjust based on actual data structure
      const advanceDetails = transactionToEdit;

      setTransactionType(tx.type);
      setAmount(tx.amount || tx.amountPaid); // Use 'amount' or 'amountPaid' based on what's available
      setDate(new Date(tx.date || tx.paymentDate).toISOString().slice(0, 10));
      setDescription(tx.description || "");

      if (tx.type === "salaryAdvance") {
        setTotalMonths(advanceDetails.totalMonths || "");
        setMonthlyDeduction(advanceDetails.monthlyDeduction || "");
      }
    } else if (isEditMode && !transactionToEdit) {
      toast.error(t("Could not load transaction data. Please go back."));
      setIsLoading(true);
    }
  }, [isEditMode, transactionToEdit, t]);

  const validate = () => {
    const newErrors = {};
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = t("Amount must be greater than zero.");
    }
    if (!date) {
      newErrors.date = t("Date is required.");
    }

    if (transactionType === "salaryAdvance") {
      if (!totalMonths || parseInt(totalMonths, 10) <= 0) {
        newErrors.totalMonths = t("Total months must be a positive number.");
      }
      if (!monthlyDeduction || parseFloat(monthlyDeduction) <= 0) {
        newErrors.monthlyDeduction = t(
          "Monthly deduction must be a positive number."
        );
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate() || !userData) return;
    setIsLoading(true);

    // --- UPDATED: Payload field names now match the 'SalaryRequest' object in the backend ---
    // The API documentation  specifies 'amount' and 'date' as the keys.
    const basePayload = {
      farmId: userData.Farm.id,
      tenantId: userData.Tenant.id,
      employeeId: parseInt(employeeId, 10),
      amount: parseFloat(amount), // Changed from 'amountPaid' to 'amount'
      date: new Date(date).getTime(), // Changed from 'paymentDate' to 'date'
      type: transactionType,
      description: description,
      category: "", // Assuming a default or empty category if not collected in the form
    };

    const advancePayload =
      transactionType === "salaryAdvance"
        ? {
            totalMonths: parseInt(totalMonths, 10),
            monthlyDeduction: parseFloat(monthlyDeduction),
          }
        : {};

    try {
      if (isEditMode) {
        // --- UPDATE LOGIC ---
        // UPDATED: Changed the endpoint URL and the payload structure to match the PUT endpoint.
        // The API expects a PUT request to '/api/payment/updateSalaryTransaction'.
        // The request body requires 'paymentId'.
        const payload = {
          ...basePayload,
          paymentId: parseInt(transactionId, 10), // Changed from 'id' to 'paymentId'
          ...advancePayload,
        };
        await axios.put("/api/payment/updateSalaryTransaction", payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Transaction updated successfully!"));
      } else {
        // --- ADD LOGIC ---
        // This part was already using the correct endpoint '/api/payment/addSalaryTransaction'[cite: 872].
        // Only the payload field names needed correction.
        const payload = { ...basePayload, ...advancePayload };
        await axios.post("/api/payment/addSalaryTransaction", payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Transaction added successfully!"));
      }
      // Navigate to returnPath if provided, otherwise to employee profile
      if (returnPath) {
        navigate(returnPath);
      } else {
        navigate(`/employees/${employeeId}`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error || t("Failed to save transaction.")
      );
      console.error("Save transaction error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isRTL = i18n.language === "ar";

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <button
          onClick={() => {
            if (returnPath) {
              navigate(returnPath);
            } else {
              navigate(`/employees/${employeeId}`);
            }
          }}
          className={styles.backButton}
        >
          <FaArrowLeft
            style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
          />
        </button>
        <h1>
          {isEditMode
            ? t("Edit Salary Transaction")
            : t("Add Salary Transaction")}
        </h1>
      </header>
      <div className={styles.formContainer}>
        <form onSubmit={handleSave} className={styles.form}>
          <div className={styles.radioGroup}>
            <label className={styles.groupLabel}>{t("Transaction Type")}</label>
            <div className={styles.radioOptions}>
              <div className={styles.radioOption}>
                <input
                  type="radio"
                  id="salary"
                  name="transactionType"
                  value="salaryPayment"
                  checked={transactionType === "salaryPayment"}
                  onChange={(e) => setTransactionType(e.target.value)}
                  disabled={isEditMode}
                />
                <label htmlFor="salary">{t("Salary")}</label>
              </div>
              <div className={styles.radioOption}>
                <input
                  type="radio"
                  id="advance"
                  name="transactionType"
                  value="salaryAdvance"
                  checked={transactionType === "salaryAdvance"}
                  onChange={(e) => setTransactionType(e.target.value)}
                  disabled={isEditMode}
                />
                <label htmlFor="advance">{t("Advance")}</label>
              </div>
              <div className={styles.radioOption}>
                <input
                  type="radio"
                  id="deduction"
                  name="transactionType"
                  value="salaryDeduction"
                  checked={transactionType === "salaryDeduction"}
                  onChange={(e) => setTransactionType(e.target.value)}
                  disabled={isEditMode}
                />
                <label htmlFor="deduction">{t("Deduction")}</label>
              </div>
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="amount">{t("Amount")}</label>
            <input
              type="number"
              step="any"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t("Enter amount")}
            />
            {errors.amount && <p className={styles.error}>{errors.amount}</p>}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="date">{t("Date")}</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            {errors.date && <p className={styles.error}>{errors.date}</p>}
          </div>

          {transactionType === "salaryAdvance" && (
            <>
              <div className={styles.inputGroup}>
                <label htmlFor="totalMonths">
                  {t("Total Months for Repayment")}
                </label>
                <input
                  type="number"
                  id="totalMonths"
                  value={totalMonths}
                  onChange={(e) => setTotalMonths(e.target.value)}
                  placeholder={t("e.g., 6")}
                />
                {errors.totalMonths && (
                  <p className={styles.error}>{errors.totalMonths}</p>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="monthlyDeduction">
                  {t("Monthly Repayment Amount")}
                </label>
                <input
                  type="number"
                  step="any"
                  id="monthlyDeduction"
                  value={monthlyDeduction}
                  onChange={(e) => setMonthlyDeduction(e.target.value)}
                  placeholder={t("Enter amount")}
                />
                {errors.monthlyDeduction && (
                  <p className={styles.error}>{errors.monthlyDeduction}</p>
                )}
              </div>
            </>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="description">{t("Description")}</label>
            <textarea
              id="description"
              rows="5"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("Add notes about the transaction")}
            ></textarea>
          </div>

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={isLoading}
            >
              {isLoading ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTransactionPage;
